// sfx/SoundProvider.jsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const SoundCtx = createContext({ play: () => Promise.resolve(false), enabled: true, toggle: () => {} });

const SFX_FILES = {
  click:    '/sfx/click.mp3',
  drop:     '/sfx/drop.mp3',
  koll: '/pokerSound/koll.mp3',
  allin: '/pokerSound/allin.mp3',
  cartOpen: '/pokerSound/cartOpen.mp3',
  fold: '/pokerSound/fold.mp3',
  bet: '/pokerSound/bet.mp3',
  check: '/pokerSound/check.mp3',
  // boardOpen: '/pokerSound/cartOpen.mp3',
  // ВАЖНО: у тебя файл называется chancel.mp3 → даём корректный путь
  cancel:   '/sfx/chancel.mp3',
  // И алиас на всякий случай, если где-то вызовут play('chancel')
  // chancel:  '/sfx/chancel.mp3',
  hit:      '/sfx/hit.mp3',
  dice:     '/sfx/roll.mp3',
  start:    '/sfx/start.mp3',
  win:      '/sfx/win.mp3',
  lose:     '/sfx/lose.mp3',
  double:   '/sfx/double.mp3',
  take:     '/sfx/take.mp3',
  pass:     '/sfx/pass.mp3',
  goodluck: '/sfx/Goodluck.mp3', // учитываем регистр в имени файла
  ok:       '/sfx/ok.mp3',
  backgammon: '/sfx/backgammon.mp3',
  nardgammon: '/sfx/nardgammon.mp3',
  registerTournament: '/sfx/registerTournament.mp3',
  allRegister:        '/sfx/allRegister.mp3',
  questBonus: '/sfx/questBonus.mp3',
  wheelFortune: '/sfx/wheelFortune.mp3',
  cardGame: '/sfx/cardGame.mp3',
  // ↓ новые названия файлов по твоей просьбе
  winner:   '/sfx/winner.mp3',
  loser:    '/sfx/loser.mp3',
};

export function SoundProvider({ children }) {
  const [enabled, setEnabled] = useState(() => localStorage.getItem('sfx:on') !== '0');
  const pool = useRef({}); // {key: [Audio, ...]}
  const unlockedRef = useRef(false);
  // Предзагрузка
  useEffect(() => {
    const a = {};
    for (const [key, url] of Object.entries(SFX_FILES)) {
      const el = new Audio(url);
      el.preload = 'auto';
      el.volume = 0.7;
      a[key] = [el];
    }
    pool.current = a;
  }, []);

  // Разблокировка на первом пользовательском жесте (iOS/Chrome автоплей)
  // Разблокировка аудио на первом ЖЕСТЕ, но не снимаем слушатели, пока реально не разблокируем
useEffect(() => {
  const tryUnlock = () => {
    if (unlockedRef.current) return;

    const pools = pool.current;
    // если пул ещё пустой (быстрый клик до предзагрузки) — подождём следующего жеста
    const allEls = Object.values(pools).flatMap(arr => arr || []);
    if (allEls.length === 0) return;

    let anyOk = false;

    allEls.forEach(el => {
      try {
        el.muted = true;
        const p = el.play();
        if (p && p.then) {
          p.then(() => {
            el.pause();
            el.currentTime = 0;
            el.muted = false;
            anyOk = true;
          }).catch(() => { /* игнорим — идём дальше */ });
        } else {
          // синхронный случай крайне редок, но поддержим
          el.pause();
          el.currentTime = 0;
          el.muted = false;
          anyOk = true;
        }
      } catch {
        /* игнорим — идём дальше */
      }
    });

    // даём промисам тик завершиться
    setTimeout(() => {
      if (anyOk) {
        unlockedRef.current = true;
        // снимаем ВСЕ слушатели только когда реально получилось
        window.removeEventListener('pointerdown', tryUnlock, true);
        window.removeEventListener('click', tryUnlock, true);
        window.removeEventListener('touchend', tryUnlock, true);
        window.removeEventListener('keydown', tryUnlock, true);
      }
    }, 0);
  };

  // разные события — на всех платформах засчитывается «жест»
  window.addEventListener('pointerdown', tryUnlock, true);
  window.addEventListener('click', tryUnlock, true);
  window.addEventListener('touchend', tryUnlock, true);
  window.addEventListener('keydown', tryUnlock, true);

  return () => {
    window.removeEventListener('pointerdown', tryUnlock, true);
    window.removeEventListener('click', tryUnlock, true);
    window.removeEventListener('touchend', tryUnlock, true);
    window.removeEventListener('keydown', tryUnlock, true);
  };
}, []);

  // useEffect(() => {
  //   const unlockAll = () => {
  //     const pools = pool.current;
  //     Object.values(pools).forEach(arr => {
  //       (arr || []).forEach(el => {
  //         try {
  //           el.muted = true;
  //           const p = el.play();
  //           if (p && p.then) {
  //             p.then(() => {
  //               el.pause();
  //               el.currentTime = 0;
  //               el.muted = false;
  //             }).catch(() => {});
  //           } else {
  //             el.pause();
  //             el.currentTime = 0;
  //             el.muted = false;
  //           }
  //         } catch {}
  //       });
  //     });
  //     window.removeEventListener('pointerdown', unlockAll, true);
  //   };
  //   window.addEventListener('pointerdown', unlockAll, { once: true, capture: true });
  //   return () => window.removeEventListener('pointerdown', unlockAll, true);
  // }, []);

  const play = useMemo(() => (name) => {
    try {
      if (!enabled) return Promise.resolve(false);
      const arr = pool.current[name];
      if (!arr) return Promise.resolve(false);
      let el = arr.find(x => x.paused);
      if (!el) {
        el = new Audio(arr[0].src);
        el.preload = 'auto';
        el.volume = arr[0].volume;
        arr.push(el);
      }
          el.currentTime = 0;

      // Если ещё НЕ разблокировано — подождём ближайший жест и попробуем сыграть после него
      if (!unlockedRef.current) {
        return new Promise((resolve) => {
          const retry = () => {
            window.removeEventListener('pointerdown', retry, true);
            window.removeEventListener('click', retry, true);
            window.removeEventListener('touchend', retry, true);
            window.removeEventListener('keydown', retry, true);

            el.currentTime = 0;
            el.play().then(() => resolve(true)).catch(() => resolve(false));
          };

          window.addEventListener('pointerdown', retry, true);
          window.addEventListener('click', retry, true);
          window.addEventListener('touchend', retry, true);
          window.addEventListener('keydown', retry, true);
        });
      }

      return el.play()
        .then(() => true)
        .catch((err) => {
          // Если блокировка автоплея — подвяжемся к СЛЕДУЮЩЕМУ жесту и повторим попытку
          const msg = String(err?.message || '');
          if (err?.name === 'NotAllowedError' || /NotAllowed/i.test(msg)) {
            return new Promise((resolve) => {
              const retry = () => {
                window.removeEventListener('pointerdown', retry, true);
                window.removeEventListener('click', retry, true);
                window.removeEventListener('touchend', retry, true);
                window.removeEventListener('keydown', retry, true);

                el.currentTime = 0;
                el.play().then(() => resolve(true)).catch(() => resolve(false));
              };

              window.addEventListener('pointerdown', retry, true);
              window.addEventListener('click', retry, true);
              window.addEventListener('touchend', retry, true);
              window.addEventListener('keydown', retry, true);
            });
          }

          console.warn('[SFX] play blocked/failed:', name, err);
          throw err; // другие ошибки пробрасываем как раньше
        });

    } catch (e) {
      console.warn('[SFX] play error:', name, e);
      return Promise.reject(e);
    }
  }, [enabled]);

  const toggle = () => {
    setEnabled(v => {
      localStorage.setItem('sfx:on', v ? '0' : '1');
      return !v;
    });
  };

  return (
    <SoundCtx.Provider value={{ play, enabled, toggle }}>
      {children}
    </SoundCtx.Provider>
  );
}

export const useSound = () => useContext(SoundCtx);

















// // sfx/SoundProvider.jsx
// import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

// const SoundCtx = createContext({ play: () => Promise.resolve(false), enabled: true, toggle: () => {} });

// const SFX_FILES = {
//   click:    '/sfx/click.mp3',
//   drop:     '/sfx/drop.mp3',
//   cancel:   '/sfx/cancel.mp3',     // было chancel.mp3 — проверь имя файла
//   hit:      '/sfx/hit.mp3',
//   dice:     '/sfx/roll.mp3',       // убрал двойной слэш
//   start:    '/sfx/start.mp3',
//   win:      '/sfx/win.mp3',
//   lose:     '/sfx/lose.mp3',
//   double:   '/sfx/double.mp3',
//   goodluck: '/sfx/Goodluck.mp3',   // как у тебя: G большая
// };

// export function SoundProvider({ children }) {
//   const [enabled, setEnabled] = useState(() => localStorage.getItem('sfx:on') !== '0');
//   const pool = useRef({}); // {key: [Audio, Audio, ...]}

//   // Предзагрузка
//   useEffect(() => {
//     const a = {};
//     for (const [key, url] of Object.entries(SFX_FILES)) {
//       const el = new Audio(url);
//       el.preload = 'auto';
//       el.volume = 0.7;
//       a[key] = [el];
//     }
//     pool.current = a;
//   }, []);

//   // Разблокировка на первом пользовательском жесте (iOS/Chrome автоплей)
//   useEffect(() => {
//     const unlockAll = () => {
//       const pools = pool.current;
//       Object.values(pools).forEach(arr => {
//         (arr || []).forEach(el => {
//           try {
//             el.muted = true;
//             const p = el.play();
//             if (p && p.then) {
//               p.then(() => {
//                 el.pause();
//                 el.currentTime = 0;
//                 el.muted = false;
//               }).catch(() => {});
//             } else {
//               el.pause();
//               el.currentTime = 0;
//               el.muted = false;
//             }
//           } catch {}
//         });
//       });
//       window.removeEventListener('pointerdown', unlockAll, true);
//     };
//     window.addEventListener('pointerdown', unlockAll, { once: true, capture: true });
//     return () => window.removeEventListener('pointerdown', unlockAll, true);
//   }, []);

//   const play = useMemo(() => (name) => {
//     try {
//       if (!enabled) return Promise.resolve(false);
//       const arr = pool.current[name];
//       if (!arr) return Promise.resolve(false);
//       let el = arr.find(x => x.paused);
//       if (!el) {
//         el = new Audio(arr[0].src);
//         el.preload = 'auto';
//         el.volume = arr[0].volume;
//         arr.push(el);
//       }
//       el.currentTime = 0;
//       // ВАЖНО: возвращаем промис — можно .then/.catch в местах вызова
//       return el.play()
//         .then(() => true)
//         .catch((err) => {
//           console.warn('[SFX] play blocked/failed:', name, err);
//           throw err;
//         });
//     } catch (e) {
//       console.warn('[SFX] play error:', name, e);
//       return Promise.reject(e);
//     }
//   }, [enabled]);

//   const toggle = () => {
//     setEnabled(v => {
//       localStorage.setItem('sfx:on', v ? '0' : '1');
//       return !v;
//     });
//   };

//   return (
//     <SoundCtx.Provider value={{ play, enabled, toggle }}>
//       {children}
//     </SoundCtx.Provider>
//   );
// }

// export const useSound = () => useContext(SoundCtx);
